import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import {
  useGetMyEarningsQuery,
  AffiliateEarning,
} from 'store/services/affiliates/affiliatesApi';
import { downloadAuthedFile } from 'utils/authedDownload';
import './EarningsHistoryTable.css';

const PAGE_SIZE = 25;

type SortBy = 'date' | 'amount';
type SortDir = 'asc' | 'desc';

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatAmount = (amountCents: number): string => `$${(amountCents / 100).toFixed(2)}`;

const buildDescription = (row: AffiliateEarning): string => {
  const base = `Referral: @${row.referredUsername} subscribed to Elite`;
  return row.planInterval ? `${base} (${row.planInterval})` : base;
};

const EarningsHistoryTable: React.FC = () => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const token = useSelector((s: RootState) => s.auth.token);

  const { data, isLoading, isFetching } = useGetMyEarningsQuery({
    page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDir,
  });

  const earnings = data?.earnings || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleDownload = async (payoutBatchId: string) => {
    try {
      await downloadAuthedFile(
        `/affiliates/me/payout-statements/${payoutBatchId}`,
        `payout-statement-${payoutBatchId}.pdf`,
        token
      );
    } catch {
      toast.error('Failed to download payout statement. Please try again.');
    }
  };

  const sortArrow = (column: SortBy) => {
    if (sortBy !== column) return null;
    return (
      <span className="affil-earn-sort-arrow" aria-hidden="true">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  return (
    <div className="affil-earn-section">
      <h4 className="affil-earn-title">Earnings History</h4>

      <div className="affil-earn-table">
        <div className="affil-earn-table-header">
          <button
            className="affil-earn-sort-button"
            onClick={() => handleSort('date')}
            type="button"
          >
            Date{sortArrow('date')}
          </button>
          <button
            className="affil-earn-sort-button"
            onClick={() => handleSort('amount')}
            type="button"
          >
            Amount{sortArrow('amount')}
          </button>
          <span>Status</span>
          <span className="affil-earn-desc-col">Description</span>
          <span></span>
        </div>

        {isLoading ? (
          <div className="affil-earn-message-row">Loading…</div>
        ) : earnings.length === 0 ? (
          <div className="affil-earn-message-row">
            No conversions yet. Share your link to start earning.
          </div>
        ) : (
          earnings.map((row) => {
            const batchId = row.payoutBatchId;
            return (
            <div key={row.id} className="affil-earn-table-row">
              <span className="affil-earn-date">{formatDate(row.subscriptionDate)}</span>
              <span className="affil-earn-amount">{formatAmount(row.amountCents)}</span>
              <span>
                <span className={`affil-earn-status affil-earn-status-${row.status}`}>
                  {row.status}
                </span>
              </span>
              <span className="affil-earn-desc affil-earn-desc-col">
                {buildDescription(row)}
              </span>
              <span className="affil-earn-download">
                {row.status === 'paid' && batchId ? (
                  <button
                    className="affil-earn-download-button"
                    onClick={() => handleDownload(batchId)}
                    title="Download payout statement"
                    aria-label="Download payout statement"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                ) : (
                  <span className="affil-earn-no-download">--</span>
                )}
              </span>
            </div>
            );
          })
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="affil-earn-pagination">
          <button
            className="affil-earn-page-button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching}
            type="button"
          >
            Previous
          </button>
          <span className="affil-earn-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="affil-earn-page-button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
            type="button"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EarningsHistoryTable;
